import { EntityDetailValueDto, PedigreeNodeDto } from '@/apiClient';
import { ENTITY_DETAIL_NAME } from '@/model/form-builder';
import { GetAnimalPedigreeQuery } from '@/publicSearch/graphql/queries.generated';

type PedigreeNode = GetAnimalPedigreeQuery['pedigree']['nodes'][number];
type PedigreeRoot = NonNullable<GetAnimalPedigreeQuery['pedigree']['root']>;

const toAnimalName = (node: { name?: string | null; ident?: string | null }) =>
  node.name || node.ident || '';

const buildDetailValue = (
  entityDetailName: string,
  value: string | null | undefined,
  entityId: number,
): EntityDetailValueDto | null =>
  value
    ? {
        entityDetailName,
        value,
        entityId,
      }
    : null;

const buildTags = (node: {
  animalId: number;
  ident?: string | null;
  dob?: string | null;
  sex?: string | null;
  status?: string | null;
  tattoo?: string | null;
}): EntityDetailValueDto[] => {
  const tags: Array<EntityDetailValueDto | null> = [
    buildDetailValue(
      ENTITY_DETAIL_NAME.ANIMAL_IDENT,
      node.ident ?? null,
      node.animalId,
    ),
    buildDetailValue(
      ENTITY_DETAIL_NAME.DATE_OF_BIRTH,
      node.dob ?? null,
      node.animalId,
    ),
    buildDetailValue(
      ENTITY_DETAIL_NAME.SEX_CODE,
      node.sex ? JSON.stringify({ label: node.sex }) : null,
      node.animalId,
    ),
    buildDetailValue(
      ENTITY_DETAIL_NAME.ANIMAL_STATUS,
      node.status ?? null,
      node.animalId,
    ),
    buildDetailValue(
      ENTITY_DETAIL_NAME.TATTOO,
      node.tattoo ?? null,
      node.animalId,
    ),
  ];

  return tags.filter((tag): tag is EntityDetailValueDto => !!tag);
};

const toPedigreeNode = (node: PedigreeNode): PedigreeNodeDto => ({
  entityGuid: String(node.animalId),
  animalName: toAnimalName(node),
  ownerName: node.ownerName ?? null,
  ownerIdent: node.ownerIdent ?? null,
  tags: buildTags(node),
});

const toPedigreeRoot = (node: PedigreeRoot): PedigreeNodeDto => ({
  entityGuid: String(node.animalId),
  animalName: toAnimalName(node),
  ownerName: node.ownerName ?? null,
  ownerIdent: node.ownerIdent ?? null,
  tags: buildTags(node),
});

export const mapPedigreeResponse = (
  pedigree: GetAnimalPedigreeQuery['pedigree'] | undefined,
): PedigreeNodeDto | undefined => {
  if (!pedigree) return undefined;

  const nodeMap = new Map<number, PedigreeNodeDto>();

  for (const node of pedigree.nodes) {
    nodeMap.set(node.animalId, toPedigreeNode(node));
  }

  if (pedigree.root) {
    const existing = nodeMap.get(pedigree.root.animalId);
    const rootNode = toPedigreeRoot(pedigree.root);
    nodeMap.set(pedigree.root.animalId, {
      ...existing,
      ...rootNode,
    });
  }

  for (const node of pedigree.nodes) {
    const current = nodeMap.get(node.animalId);
    if (!current) continue;

    if (node.sireId) {
      current.sire = nodeMap.get(node.sireId);
    }

    if (node.damId) {
      current.dam = nodeMap.get(node.damId);
    }
  }

  if (pedigree.root) {
    return nodeMap.get(pedigree.root.animalId);
  }

  const firstNode = pedigree.nodes[0];
  return firstNode ? nodeMap.get(firstNode.animalId) : undefined;
};
